import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { CampusArea } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-checkbox.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/cr-tabs.js';
import '@chancenwerk/design/components/fonts.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import '../components/campus-component-eventbox.js';
import '../components/campus-component-eventlist.js';
import '../components/campus-component-section-lesson-list.js';
import '../components/campus-component-sticky-search-bar.js';
import { CampusDialogEditSection } from '../components/dialogs/campus-dialog-edit-section.js';
import { CampusRoutes } from '../routes.js';

/**
 * @customElement campus-page-editorial-lounge-external
 */
@customElement('campus-page-editorial-lounge-external')
export class CampusPageEditorialLoungeExternal extends ChemistryLitElement {
  // Filtering properties
  @state() private readonly _userCheckedSubjects = new RXController<string[]>(this, []); // TODO unused but we maybe want filtering later for employee roles or different overall topics
  @state() private readonly _userCheckedGrades = new RXController<number[]>(this, []); // TODO unused but we maybe want filtering later for employee roles or different overall topics
  @state() private readonly _userSearchText = new RXController<string>(this, '');

  @state() private readonly _updateList = 0;

  @state() size = new WindowSizeController(this);

  override render() {
    return this._content();
  }

  private _content() {
    return html`
      <cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
        <cr-flex space="lg">
          <cr-h2> Inhalte für externe Lehrpersonen </cr-h2>
          <cr-text>
            Diese Inhalte sind nicht von Schüler:innen einsehbar, sondern nur von Mitarbeitenden und externen
            Lehrpersonen.
          </cr-text>
        </cr-flex>
        ${permissionGuard(
          ChancenPermission.campus.EDIT_CONTENT_EXTERNAL,
          () => html`<cr-flex horizontal wrap space="sm">
            <cr-button
              ${css({ minWidth: '230px' })}
              flex
              icon="add"
              @click=${async () => {
                await CampusDialogEditSection.showPopup({
                  draggable: this.size.mobile || this.size.tablet,
                  modal: true,
                  props: {
                    area: CampusArea.EXTERNAL_EMPLOYEES,
                  },
                });
              }}
            >
              Themenbereich
            </cr-button>
            <cr-button
              ${css({ minWidth: '230px' })}
              flex
              icon="add"
              href=${CampusRoutes.lessons + `/edit?area=${CampusArea.EXTERNAL_EMPLOYEES}`}
              target="_blank"
            >
              Lektion
            </cr-button>
          </cr-flex>`,
          {
            context: ANY_CONTEXT,
          },
        )}

        <campus-component-sticky-search-bar
          @search-text-changed=${(e: ValueEvent<string>) => {
            if (this._userSearchText.value === e.detail.value) return;
            this._userSearchText.next(e.detail.value);
          }}
        >
        </campus-component-sticky-search-bar>

        <cr-flex space="md">
          ${guard(
            [
              this._userCheckedGrades.value,
              this._userCheckedSubjects.value,
              this._userSearchText.value,
              this._updateList,
            ],
            () => html`
              <campus-component-section-lesson-list
                .mode=${'edit'}
                .userCheckedGrades=${this._userCheckedGrades.value}
                .userCheckedSubjects=${this._userCheckedSubjects.value}
                .userSearchText=${this._userSearchText.value}
                .area=${CampusArea.EXTERNAL_EMPLOYEES}
              ></campus-component-section-lesson-list>
            `,
          )}
        </cr-flex>
      </cr-flex>
    `;
  }
}
